import { useState, useCallback } from 'react';
import { useToast } from '@practium/ui';
import type { FieldDiaryEntry, NoteFormData } from '../types';
import { NoteSchema } from '../schemas/note.schema';

const DEFAULT_NOTE_FORM: NoteFormData = {
	title: '',
	date: '',
	category: '',
	note: '',
	createdBy: '',
};

export interface UseNoteFormOptions {
	onSubmit: (form: NoteFormData) => Promise<FieldDiaryEntry>;
	onSuccess?: (saved: FieldDiaryEntry) => void;
}

export const useNoteForm = ({ onSubmit, onSuccess }: UseNoteFormOptions) => {
	const toast = useToast();
	const [form, setForm] = useState<NoteFormData>({ ...DEFAULT_NOTE_FORM });
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isSaving, setIsSaving] = useState(false);

	const set = useCallback((field: keyof NoteFormData, value: string) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		setErrors((prev) => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	}, []);

	const populate = useCallback((data: NoteFormData) => {
		setForm({ ...data });
		setErrors({});
	}, []);

	const reset = useCallback(() => {
		setForm({ ...DEFAULT_NOTE_FORM });
		setErrors({});
	}, []);

	const handleSubmit = useCallback(async () => {
		const result = NoteSchema.safeParse(form);
		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			for (const issue of result.error.issues) {
				const key = issue.path[0] as string;
				if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
			}
			setErrors(fieldErrors);
			toast.error('Corrija os campos destacados antes de salvar.');
			return;
		}

		setIsSaving(true);
		try {
			const saved = await onSubmit(form);
			toast.success('Nota salva com sucesso!');
			reset();
			onSuccess?.(saved);
		} catch (err) {
			const msg = err instanceof Error ? err.message : 'Erro ao salvar nota';
			toast.error(msg);
		} finally {
			setIsSaving(false);
		}
	}, [form, toast, reset, onSubmit, onSuccess]);

	return { form, errors, isSaving, set, populate, reset, handleSubmit };
};
